import { AlertTriangle } from "lucide-react";
import { Component, type ErrorInfo, type ReactNode } from "react";
import { EmptyState } from "./empty/EmptyState";
import { Button } from "./ui/button";

/**
 * Error boundary for the routed page area. Mounted INSIDE the portal shell so
 * a crashed page never takes the chrome with it — the sidebar, topbar, and
 * palette keep working and any route change auto-resets the boundary.
 *
 * It also owns the stale-deploy failure: a lazy route chunk that 404s after a
 * new build ships rejects through Suspense and lands here. That case gets a
 * reload-first fallback because React caches the rejected chunk promise —
 * re-rendering would just re-throw, so "Try again" is deliberately absent.
 */

interface RouteErrorBoundaryProps {
  children: ReactNode;
  /** Route identity — a navigation that changes it clears a shown error. */
  resetKey: string;
}

interface RouteErrorBoundaryState {
  error: unknown;
  hasError: boolean;
}

// Chunk-load failure messages per engine (Chrome / Firefox / Safari / webpack
// vocabulary respectively) — there is no structured error type for this.
const CHUNK_LOAD_PATTERNS = [
  /failed to fetch dynamically imported module/i,
  /error loading dynamically imported module/i,
  /importing a module script failed/i,
  /chunkloaderror/i,
];

function isChunkLoadError(error: unknown): boolean {
  const message =
    error instanceof Error
      ? `${error.name} ${error.message}`
      : typeof error === "string"
        ? error
        : "";
  return CHUNK_LOAD_PATTERNS.some((pattern) => pattern.test(message));
}

export class RouteErrorBoundary extends Component<
  RouteErrorBoundaryProps,
  RouteErrorBoundaryState
> {
  state: RouteErrorBoundaryState = { error: null, hasError: false };

  static getDerivedStateFromError(error: unknown): RouteErrorBoundaryState {
    return { error, hasError: true };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    // No external error reporting exists; the console is the only sink an
    // operator can be pointed at.
    console.error("[hub] route render failed", error, info.componentStack);
  }

  componentDidUpdate(previous: RouteErrorBoundaryProps): void {
    // Navigating away must not keep showing a stale crash — the shell's nav
    // stays interactive precisely so the user can leave the broken page.
    if (this.state.hasError && previous.resetKey !== this.props.resetKey) {
      this.setState({ error: null, hasError: false });
    }
  }

  private readonly retry = (): void => {
    this.setState({ error: null, hasError: false });
  };

  private readonly reload = (): void => {
    globalThis.location?.reload();
  };

  render(): ReactNode {
    if (!this.state.hasError) return this.props.children;

    const chunkError = isChunkLoadError(this.state.error);
    return (
      <div
        className="flex flex-col items-center pt-[var(--empty-state-py-page)]"
        data-testid="route-error-fallback"
        role="alert"
      >
        <span
          aria-hidden="true"
          className="flex size-[var(--icon-button-lg)] items-center justify-center rounded-full bg-[var(--color-danger-soft)] text-[var(--color-danger)]"
        >
          <AlertTriangle className="size-5" />
        </span>
        {chunkError ? (
          <EmptyState
            action={
              <Button data-testid="route-error-reload" onClick={this.reload} variant="primary">
                Reload page
              </Button>
            }
            body="Reload to use the latest version."
            inline
            title="This page is out of date."
          />
        ) : (
          <EmptyState
            action={
              <div className="flex flex-wrap items-center justify-center gap-3">
                <Button data-testid="route-error-retry" onClick={this.retry} variant="primary">
                  Try again
                </Button>
                <Button data-testid="route-error-reload" onClick={this.reload} variant="secondary">
                  Reload page
                </Button>
              </div>
            }
            inline
            title="Couldn’t load this page."
          />
        )}
      </div>
    );
  }
}
